import axios from 'axios';
import { useEffect, useRef, useState, useMemo } from 'react';
import styled from 'styled-components';
import { colors } from '../../assets/colors';
import { RowView, Text } from '../../helpers';
import { IInvoice } from '../../interfaces';
import { setGlobalState, useGlobalState } from '../../state';
import RoundBox from '../RoundBox/RoundBox';
import { dateTransform, getPaymentOptionTitle } from '../../functions';
import { PaymentOption, initialOptionIndex, termsForOption } from '../../functions/paymentOptionLabel';
import { waitForBnplIntent } from '../../api/bnplIntent';
import { isStandardInvoice } from '../../functions/checkoutFlow';
import { normalizePaymentProvider } from '../../functions/paymentProvider';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div``;

interface DueDaysProps {
    selected?: boolean;
}

type DueDays = {
    invoiceId: IInvoice['uuid'];
};

type DueDaysOption = PaymentOption;

const Dot = styled.div<DueDaysProps>`
    display: inline-block;
    height: 20px;
    width: 20px;
    margin-right: 1vw;
    border-width: ${(props) => (props.selected ? '6px' : '1px')};
    border-style: solid;
    border-color: ${(props) => (props.selected ? colors.main : colors.grey)};
    border-radius: 100%;
`;

const TextWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const DaysAmount = styled.div<DueDaysProps>`
    background: ${colors.faded};
    padding: 24px;
    border-radius: 6px;
    margin-top: 2vh;
    opacity: ${(props) => (props.selected ? 1 : 0.7)};
    border-width: 0.5;
    border-color: ${(props) => (props.selected ? colors.main : colors.white)};
    border-style: solid;
    box-sizing: content-box;
    cursor: pointer;
    &:hover {
        opacity: 1;
    }
`;

const DueDays = ({ invoiceId }: DueDays) => {
    const CHANGE_KR_TO_PERCENTAGE = 10000;

    const [invoice] = useGlobalState('invoice');
    const standard = isStandardInvoice(invoice.invoice_type);
    const stripe = normalizePaymentProvider(invoice.payment_provider) === 'stripe';
    const [loading] = useGlobalState('loading');
    const [optionStatus] = useGlobalState('optionStatus');
    const [dueDaysOptions, setDueDaysOption] = useState<DueDaysOption[]>([]);
    const [selectedDueDays, setSelectedDueDays] = useState(0);
    const [optionError, setOptionError] = useState<string | null>(null);
    const appliedSeller = useRef(false);
    const { t } = useTranslation();

    const iniTialTotalAmount = useMemo(() => {
        return invoice.total_amount;
    }, [invoice.uuid]);

    const addDays = (date: Date | undefined | null, days: number) => {
        if (!date) {
            return;
        }
        const result = new Date(date);
        result.setDate(result.getDate() + days);
        const formatedDueDate = result.toUTCString();
        const i = formatedDueDate.indexOf(' ');
        return formatedDueDate.slice(i, formatedDueDate.length - 12);
    };

    useEffect(() => {
        const getBnplOptions = async (id: string) => {
            if (id) {
                setGlobalState('loading', true);
                try {
                    const response = await axios.post<DueDaysOption[]>(
                        import.meta.env.VITE_API_BASE_URL +
                            'web/invoice/' +
                            id +
                            '/get_bnpl_options/'
                    );
                    setDueDaysOption(response.data);
                } catch (error) {
                    console.error('Error fetching BNPL options:', error);
                    setDueDaysOption([]);
                } finally {
                    setGlobalState('loading', false);
                }
            }
        };
        if (standard) {
            return;
        }
        appliedSeller.current = false;
        getBnplOptions(invoiceId);
    }, [invoiceId]);

    const feeLabel = (item: DueDaysOption) => {
        if (Number(item.cost_excl_vat_amount) === 0) {
            return '';
        }
        const currency = item.currency || 'SEK';
        if (iniTialTotalAmount > CHANGE_KR_TO_PERCENTAGE) {
            return `(+ ${Number(
                (Number(item.cost_excl_vat_amount) / iniTialTotalAmount) * 100
            ).toFixed(2)}%)`;
        }
        return `(+ ${Number(item.cost_excl_vat_amount).toFixed(2)} ${currency})`;
    };

    const applyOption = async (index: number, lock: boolean) => {
        const selectedOption = dueDaysOptions[index];
        if (!selectedOption || !invoice.uuid) {
            return;
        }
        setSelectedDueDays(index);
        setOptionError(null);
        if (lock) {
            setGlobalState('checkoutLocked', true);
        }

        const dueDate = new Date(invoice.created ?? '');
        if (selectedOption.due_days != null) {
            dueDate.setDate(dueDate.getDate() + selectedOption.due_days);
        }

        setGlobalState('loading', true);
        try {
            let intentUuid = selectedOption.intent_uuid;
            let status = (selectedOption.status ?? 'approved').toLowerCase();
            const terms = termsForOption(selectedOption);

            if (!intentUuid && terms) {
                const created = await waitForBnplIntent(invoice.uuid, terms);
                intentUuid = created.intent_uuid;
                status = (created.status ?? 'approved').toLowerCase();
            }

            setGlobalState('optionStatus', status);

            if (status === 'denied' || status === 'error') {
                setOptionError(t('payment_option_denied'));
                setGlobalState('checkoutLocked', false);
                return;
            }
            if (status === 'pending' || status === 'created') {
                return;
            }
            if (!intentUuid) {
                return;
            }

            const { data } = await axios.post(
                import.meta.env.VITE_API_BASE_URL +
                    'web/invoice/' +
                    invoice.uuid +
                    '/get_updated_lines/',
                { intent_uuid: intentUuid }
            );
            setGlobalState('invoice', {
                ...invoice,
                due_days: selectedOption.due_days ?? invoice.due_days,
                due_date: selectedOption.due_days != null ? dueDate : invoice.due_date,
                total_amount: data.total_amount,
                vat: Number(data.vat),
                lines: data.lines,
                intent_uuid: intentUuid
            });
        } catch (error) {
            console.error('Error applying payment option:', error);
            setOptionError(t('payment_option_denied'));
            if (lock) {
                setGlobalState('checkoutLocked', false);
            }
        } finally {
            setGlobalState('loading', false);
        }
    };

    useEffect(() => {
        if (appliedSeller.current || dueDaysOptions.length === 0) {
            return;
        }
        appliedSeller.current = true;
        const index = initialOptionIndex(dueDaysOptions, invoice.due_days);
        void applyOption(index, false);
    }, [dueDaysOptions]);

    const waiting = optionStatus === 'pending' || optionStatus === 'created';

    return (
        <Wrapper>
            {/* No loader here: the customer details below already cover the page with one. */}
            <RoundBox>
                <Text
                    i={standard ? t('due_date') : t('payment_method')}
                    fontWeight="bold"
                    header
                />
                {!standard && <Text i={t('payment_option_lock_hint')} />}
                {standard && (
                    <DaysAmount selected={true}>
                        <RowView>
                            <RowView>
                                <TextWrapper>
                                    <Text i={dateTransform(invoice.due_date, invoice)} />
                                </TextWrapper>
                            </RowView>
                        </RowView>
                    </DaysAmount>
                )}
                {waiting ? <Text i={t('payment_option_waiting')} /> : null}
                {optionError ? <Text i={optionError} color={colors.main} /> : null}
                {dueDaysOptions.map((item, index) => {
                    const title = [getPaymentOptionTitle(item, t), feeLabel(item)]
                        .filter(Boolean)
                        .join(' ');
                    return (
                        <DaysAmount
                            selected={selectedDueDays === index}
                            key={item.intent_uuid ?? `${item.terms_type}-${index}`}
                            onClick={() => {
                                if (!loading) {
                                    void applyOption(index, true);
                                }
                            }}>
                            <RowView>
                                <RowView>
                                    <Dot selected={selectedDueDays === index} />
                                    <TextWrapper>
                                        <Text
                                            fontWeight="bold"
                                            i={title}
                                            color={colors.dark2}
                                        />
                                        {item.due_days != null && (
                                            <Text
                                                i={`${t('due_date')} ${addDays(
                                                    invoice.created,
                                                    item.due_days
                                                )}`}
                                            />
                                        )}
                                        {stripe ? (
                                            <Text i={t('paymentMethodsNextStep')} />
                                        ) : null}
                                    </TextWrapper>
                                </RowView>
                            </RowView>
                        </DaysAmount>
                    );
                })}
            </RoundBox>
        </Wrapper>
    );
};

export default DueDays;
